import {GameStateEnums, ModeSettingEnum} from '../../../../../shared/GameEnums'
import {UIGameManager} from '../UIGameManager'
import {UIGamePlayerInformationLongestRoadView} from './UIGamePlayerInformationLongestRoadView'
import {PlayerValidator, MechanicValidatorLongestRoad} from '@colonist/game-mechanic-validators'
import {InGameAchievement} from '../ui_game_achievement/UIGameAchievementController'
import {Sound} from '../../Sound'
import {TranslationObject} from '@colonist/i18n'
import {UIGameSpriteHandler} from '../../UIGameSpriteHandler'
import {Sprites} from '../UIGameImageDictionary'
import {MechanicLongestRoadState, PlayerColor} from '@colonist/game-states'
import UIGameEventEnums = GameStateEnums.UIGameEventEnums

export abstract class UIGamePlayerInformationLongestRoadController {
    protected readonly uiGameManager: UIGameManager
    protected playerInformationLongestRoadViews: UIGamePlayerInformationLongestRoadView[]

    private readonly longestRoadValidator: MechanicValidatorLongestRoad
    private lastPlayerToHoldLongestRoad: PlayerColor

    constructor(uiGameManager: UIGameManager, longestRoadState: MechanicLongestRoadState) {
        this.uiGameManager = uiGameManager
        this.uiGameManager.eventController.registerUIGameMechanic(this)
        this.playerInformationLongestRoadViews = []

        this.longestRoadValidator = new MechanicValidatorLongestRoad(this.uiGameManager.gameController.getPlayerValidators(), this.uiGameManager.gameController.mapValidator, longestRoadState)
        this.lastPlayerToHoldLongestRoad = this.longestRoadValidator.getPlayerWithLongestRoadCard()

        this.initPlayerLongestRoadViews()
    }

    onEvent(data: any, eventType: UIGameEventEnums) {
        switch(eventType) {
            case UIGameEventEnums.PlayerControllerStateUpdated: this.updateLongestRoadStatus(data); break
            case UIGameEventEnums.CanvasResized: this.initPlayerLongestRoadViews(); break
            default: break
        }
    }

    private getPlayerInformationLongestRoadView(player: PlayerColor): UIGamePlayerInformationLongestRoadView | undefined {
        for(const playerInformationLongestRoadView of this.playerInformationLongestRoadViews) {
            if(playerInformationLongestRoadView.playerState.color == player) return playerInformationLongestRoadView
        }
        return undefined
    }

    private initPlayerLongestRoadViews() {
        this.playerInformationLongestRoadViews.forEach((view) => view.destroy())
        this.playerInformationLongestRoadViews = []

        for(const playerState of this.uiGameManager.gameState.players) {
            const newPlayerInformationLongestRoadView = this.createLongestRoadView(playerState)
            this.playerInformationLongestRoadViews.push(newPlayerInformationLongestRoadView)
        }
    }

    protected abstract createLongestRoadView(playerState: PlayerValidator): UIGamePlayerInformationLongestRoadView

    private updateLongestRoadStatus(animate: boolean) {
        this.resetLastLongestRoadIfNoPlayerHasLongestRoad()
        for(const player of this.uiGameManager.gameState.players) {
            const longestRoadPlayerState = this.longestRoadValidator.getLongestRoadPlayerState(player.color)
            if(!longestRoadPlayerState.hasLongestRoad) continue
            if(player.color == this.lastPlayerToHoldLongestRoad) continue

            this.playerAchievedLongestRoad(player, animate)
        }
    }

    private resetLastLongestRoadIfNoPlayerHasLongestRoad() {
        for(const player of this.uiGameManager.gameState.players) {
            const longestRoadPlayerState = this.longestRoadValidator.getLongestRoadPlayerState(player.color)
            if(longestRoadPlayerState.hasLongestRoad) return
        }
        this.lastPlayerToHoldLongestRoad = PlayerColor.None
    }

    private playerAchievedLongestRoad(playerState: PlayerValidator, animate: boolean) {
        const playerInformationLongestRoadView = this.getPlayerInformationLongestRoadView(playerState.color)
        if(playerInformationLongestRoadView == undefined) return

        const title = this.getAchievementTitle()
        const longestRoadPlayerState = this.longestRoadValidator.getLongestRoadPlayerState(playerState.color)
        const longestRoadScore = longestRoadPlayerState.longestRoad
        const texture = UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.icon_longest_road_highlight)

        const achievement: InGameAchievement = {
            title: title,
            achievementOwnerNew: playerState.color,
            achievementOwnerPrevious: this.lastPlayerToHoldLongestRoad,
            achievementScore: longestRoadScore,
            victoryPointChange: 2,
            achievementDurationInSeconds: 1.5,
            soundType: Sound.SoundType.AchievementLongestRoad,
            bottomDescription: {key: 'strings:game.achievements.bottomDescription.2VPs'},
            iconTexture: texture,
        }

        this.lastPlayerToHoldLongestRoad = playerState.color

        this.uiGameManager.eventController.sendEvent(achievement, UIGameEventEnums.PlayerReceivedAchievement)
    }

    private getAchievementTitle(): TranslationObject {
        const gameModeSetting = this.uiGameManager.gameState.gameModeData.modeSetting
        switch(gameModeSetting) {
            case ModeSettingEnum.Seafarers4P:
            case ModeSettingEnum.Seafarers56P:
            case ModeSettingEnum.CitiesAndKnightsSeafarers4P:
            case ModeSettingEnum.CitiesAndKnightsSeafarers56P:
                return {key: 'strings:game.playerInformation.longestRoad.title', options: {context: 'seafarers'}}
            default:
                return {key: 'strings:game.playerInformation.longestRoad.title'}
        }
    }
}
